import React, { useContext, useMemo } from 'react';
import { CaretDownFilled, CaretUpFilled } from '@ant-design/icons';
import { Spin } from 'antd';

import RemoteAccessDashboardContext from '@totem/components/services/remoteAccess/v2/summary/remoteAccessDashboardContext';
import IBStatistic, {
  statisticsStyles,
} from '@totem/components/ui_components/widgets/statistic/IBStatistic';
import OrganizationContext from '@totem/components/userContext/organizationContext/organizationContext';
import { isNotNull, isNull } from '@totem/utilities/common';

const styles = {
  table: {
    //borderSpacing: '16px',
  },
  firstCell: {
    paddingRight: '16px',
    width: '20%',
  },
  lastCell: {
    paddingLeft: '16px',
    width: '20%',
  },
  middleCell: {
    padding: '16px',
    width: '20%',
  },
};

const commonValueStyle = { fontSize: '32px' };

const PropertySummary = () => {
  const { buildings } = useContext(OrganizationContext);
  const { dashboardData, organizationSummaryData, organizationSummaryLoading } =
    useContext(RemoteAccessDashboardContext);

  const totalProperties = useMemo(() => {
    if (isNotNull(buildings)) return buildings.length;

    if (isNotNull(dashboardData))
      return dashboardData.propertySummary.totalProperties;

    return 0;
  }, [buildings, dashboardData]);

  const atRiskOrganizations = useMemo(() => {
    if (isNull(organizationSummaryData)) {
      return 0;
    }
    return organizationSummaryData.filter((chk) => chk.totalRisk > 0.3).length;
  }, [organizationSummaryData]);

  const organizationAdoption = useMemo(() => {
    if (isNull(organizationSummaryData)) {
      return 0;
    }
    const active = organizationSummaryData.filter(
      (chk) => chk.activeUsers > 0,
    ).length;
    const totalOrganizations = organizationSummaryData.length;
    if (totalOrganizations === 0) {
      return 0;
    }
    return (active / totalOrganizations) * 100;
  }, [organizationSummaryData]);

  if (
    isNull(dashboardData) ||
    isNull(dashboardData.propertySummary) ||
    isNull(dashboardData.avgWeeklyUniqueSessions)
  ) {
    return <span />;
  }

  return (
    <table width="100%" style={styles.table}>
      <tbody>
        <tr>
          <td style={styles.firstCell}>
            <IBStatistic
              title="Properties"
              subTitle={'Using Remote Access'}
              value={`${dashboardData.propertySummary.totalProperties} of ${totalProperties}`}
              precision={0}
              style={statisticsStyles.neutral}
              valueStyle={commonValueStyle}
              valueSuffixStyle={commonValueStyle}
            />
          </td>
          <td style={styles.middleCell}>
            <IBStatistic
              title="Property Adoption"
              subTitle={'% of Properties using Remote Access'}
              value={dashboardData.propertySummary.currentAdaptation}
              precision={1}
              valueSuffix={
                <span>
                  %&nbsp;
                  {dashboardData.propertySummary.currentAdaptation ===
                  dashboardData.propertySummary.previousAdaptation ? (
                    <span />
                  ) : dashboardData.propertySummary.currentAdaptation >
                    dashboardData.propertySummary.previousAdaptation ? (
                    <CaretUpFilled
                      style={{ color: statisticsStyles.ok.color }}
                    />
                  ) : (
                    <CaretDownFilled
                      style={{ color: statisticsStyles.critical.color }}
                    />
                  )}
                </span>
              }
              style={statisticsStyles.neutral}
              valueStyle={commonValueStyle}
              valueSuffixStyle={commonValueStyle}
            />
          </td>
          <td style={styles.middleCell}>
            <IBStatistic
              title="Weekly Unique Sessions"
              subTitle={'Average of recorded Access Sessions'}
              value={dashboardData.avgWeeklyUniqueSessions.currentWeeklyAverage}
              valueSuffix={
                dashboardData.avgWeeklyUniqueSessions.currentWeeklyAverage ===
                dashboardData.avgWeeklyUniqueSessions.previousWeeklyAverage ? (
                  <span />
                ) : dashboardData.avgWeeklyUniqueSessions.currentWeeklyAverage >
                  dashboardData.avgWeeklyUniqueSessions
                    .previousWeeklyAverage ? (
                  <CaretUpFilled style={{ color: statisticsStyles.ok.color }} />
                ) : (
                  <CaretDownFilled
                    style={{ color: statisticsStyles.critical.color }}
                  />
                )
              }
              precision={0}
              style={statisticsStyles.neutral}
              valueStyle={commonValueStyle}
              valueSuffixStyle={commonValueStyle}
            />
          </td>
          <td style={styles.middleCell}>
            <IBStatistic
              title="User Organization Adoption"
              subTitle={'% of authorized orgs using remote access'}
              value={
                !organizationSummaryLoading ? (
                  organizationAdoption
                ) : (
                  <Spin
                    description={'Loading...'}
                    style={statisticsStyles.neutral}
                  />
                )
              }
              valueSuffix={!organizationSummaryLoading ? '%' : ''}
              precision={1}
              style={statisticsStyles.neutral}
              valueStyle={commonValueStyle}
              valueSuffixStyle={commonValueStyle}
            />
          </td>
          <td style={styles.lastCell}>
            <IBStatistic
              title="At Risk Organizations"
              subTitle={'Authorized orgs w/Risk Score above 3.0'}
              value={
                !organizationSummaryLoading ? (
                  atRiskOrganizations
                ) : (
                  <Spin
                    description={'Loading...'}
                    style={statisticsStyles.neutral}
                  />
                )
              }
              precision={0}
              style={statisticsStyles.neutral}
              valueStyle={commonValueStyle}
              valueSuffixStyle={commonValueStyle}
            />
          </td>
        </tr>
      </tbody>
    </table>
  );
};

export default PropertySummary;
